import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { AlertCircle, CheckCircle2, CloudUpload, Info, User, Users, X } from "lucide-react";
import { type FormEvent, type ReactNode, useEffect, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import logo from "@/assets/logo-light.png";
import { readImage } from "@/components/avatar";
import { type ConsentText, ConsentTextModal } from "@/components/consent-texts";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/dialog";
import { Field, Input } from "@/components/ui/field";
import { FkSelect } from "@/components/ui/fk-select";
import { Select } from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { api, errorMessage } from "@/services/api";
import type { TranslationKey } from "@/constants/i18n";
import { cn } from "@/utils/utils";
import {
  ACQUISITION,
  GENDER,
  MEMBERSHIP_TYPE,
  type Member,
  PREFIX,
  SUFFIX,
  fullName,
} from "@/pages/MembersPage";
import { useTranslation } from "@/store/locale";
import { useTypedSearch } from "@/hooks/use-typed-search";

const TIERS = ["member", "affiliates", "employee", "complimentary"] as const;
type Tier = (typeof TIERS)[number];

const searchSchema = z.object({
  tier: z.enum(TIERS).catch("member"),
  /** A primary's account number: the detail page sends a clerk here to add a supplementary. */
  parent: z.string().optional().catch(undefined),
});

type AccountType = "primary" | "supplementary";

/** Which of the three steps is on screen: who to add, a gate, the form. */
type Step = 1 | 2 | 3;

/** A required field's label. One asterisk, one colour, everywhere. */
const req = (text: string) => (
  <>
    {text} <span className="text-danger">*</span>
  </>
);

/**
 * The tiers that can only ever be primary. An affiliate and an employee have
 * no supplementary card number to be given — see the matrix in the backend's
 * `member/numbering.py` — so the flow never asks.
 */
const PRIMARY_ONLY: Tier[] = ["affiliates", "employee"];

/** The tiers enrolled as a benefit, never over a counter. */
const BY_BENEFIT: Tier[] = ["affiliates", "employee", "complimentary"];

/** Where a home address is part of the record. */
const NEEDS_ADDRESS: Tier[] = ["member", "employee"];

/** Nobody under this age can hold a membership. */
const MINIMUM_AGE = 18;

/** Whole years between a date and today; -1 for no date, so a blank is not 0. */
const years = (birthday: string | undefined) => {
  if (!birthday) return -1;
  const then = new Date(birthday);
  const now = new Date();
  const whole = now.getFullYear() - then.getFullYear();
  const before =
    now.getMonth() < then.getMonth() ||
    (now.getMonth() === then.getMonth() && now.getDate() < then.getDate());
  return before ? whole - 1 : whole;
};

/**
 * The primary a supplementary hangs off, as the gate needs to show it. The
 * verdict is the status code — this shape only ever comes back for an account
 * that can take another card.
 */
interface PrimaryAccount {
  member_id: number;
  account_number: string;
  first_name: string;
  middle_name: string | null;
  last_name: string | null;
  date_of_birth: string;
  valid_until: string | null;
  no_expiry: boolean;
  membership_tier: Tier;
  membership_type: string;
  active_supplementaries: number;
}

/**
 * The enrolment flow: who to add, a gate where one is owed, then the form.
 *
 * A gate is a step that asks one thing and will not open the form until the
 * server has answered — the primary a supplementary hangs off, the employee
 * number an employee membership is for. Everything else is a section of the
 * one form rather than a step of its own: a form the eye can see the whole of
 * beats a machine that reveals it a question at a time.
 */
function NewMemberPage() {
  const t = useTranslation();
  const { tier, parent: parentNumber } = useTypedSearch(searchSchema);
  const navigate = useNavigate();
  const primaryOnly = PRIMARY_ONLY.includes(tier);
  const [accountType, setAccountType] = useState<AccountType | null>(
    primaryOnly ? "primary" : parentNumber ? "supplementary" : null,
  );
  // What each gate produced, and what the form then treats as given.
  const [parent, setParent] = useState<PrimaryAccount | null>(null);
  const [employeeNumber, setEmployeeNumber] = useState("");
  const [hr, setHr] = useState<HrEmployee | null>(null);
  // Nothing to ask when only one answer is possible, so that flow starts later.
  const [step, setStep] = useState<Step>(
    primaryOnly ? (tier === "employee" ? 2 : 3) : parentNumber ? 2 : 1,
  );
  const cancel = () => void navigate("/members");

  return (
    <div className="grid min-h-dvh lg:grid-cols-[minmax(18rem,28%)_1fr]">
      <aside className="bg-brand-700 relative hidden overflow-hidden p-8 text-white lg:block">
        <img src={logo} alt="S&R" className="h-10 w-auto" />
        <p className="text-title-2 mt-24 max-w-xs leading-snug">
          {t("new.taglineFor").replace(
            "{tier}",
            accountType === "supplementary"
              ? `${t(TIER_LABELS[tier])} ${t("mem.supplementary")}`
              : t(TIER_LABELS[tier]),
          )}
        </p>
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-[radial-gradient(ellipse_at_bottom,#0b1230,#1e2a5a_50%,transparent_75%)]">
          <div className="absolute inset-x-[-10%] top-1/3 h-10 -rotate-6 bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.7),transparent)] opacity-60" />
        </div>
      </aside>

      <main className="bg-canvas relative px-6 py-10 md:px-12">
        <Button variant="secondary" onClick={cancel} className="absolute top-6 right-8">
          {t("common.cancel")}
        </Button>
        {step === 1 ? (
          <WhoToAdd
            value={accountType}
            onChange={setAccountType}
            onContinue={() => setStep(accountType === "supplementary" ? 2 : 3)}
          />
        ) : step === 2 && accountType === "supplementary" ? (
          <PrimaryGate
            initial={parentNumber ?? ""}
            onConfirmed={(found) => {
              setParent(found);
              setStep(3);
            }}
          />
        ) : step === 2 ? (
          <EmployeeGate
            onConfirmed={(number, found) => {
              setEmployeeNumber(number);
              setHr(found);
              setStep(3);
            }}
          />
        ) : (
          <BasicForm
            tier={parent?.membership_tier ?? tier}
            accountType={accountType ?? "primary"}
            parent={parent}
            employeeNumber={employeeNumber}
            hr={hr}
            onCreated={(m) =>
              void navigate(`/members/${m.id}`)
            }
          />
        )}
      </main>
    </div>
  );
}

/**
 * The parent gate. Debounced as the number is typed, because a clerk types an
 * account number they were told rather than one they picked from a list, and
 * the answer to "is this the right person" is the name beside it.
 *
 * The server owns the verdict: six things can be wrong with a primary and each
 * has its own sentence. This asks the same function the create will, so what
 * passes here passes there.
 */
function PrimaryGate({
  initial = "",
  onConfirmed,
}: {
  /** A number the caller already knows — the detail page's own account. */
  initial?: string;
  onConfirmed: (parent: PrimaryAccount) => void;
}) {
  const t = useTranslation();
  const [typed, setTyped] = useState(initial);
  const [number, setNumber] = useState(initial.trim());
  const [confirming, setConfirming] = useState(false);

  // 400ms: long enough that a ten-digit number is one request, short enough
  // that the name appears while the person is still looking at the field.
  useEffect(() => {
    const timer = setTimeout(() => setNumber(typed.trim()), 400);
    return () => clearTimeout(timer);
  }, [typed]);

  const found = useQuery({
    queryKey: ["members", "primary", number],
    queryFn: async ({ signal }) =>
      (
        await api.get<PrimaryAccount>(`/members/primary/${encodeURIComponent(number)}`, {
          signal,
        })
      ).data,
    enabled: number.length > 0,
    retry: false,
  });
  const parent = found.data ?? null;
  const looking = number.length > 0 && (found.isFetching || typed.trim() !== number);

  return (
    <div className="mx-auto mt-10 max-w-xl sm:mt-16">
      <h1 className="text-title-1 text-heading text-center">{t("mem.supplementary")}</h1>
      <Card className="mt-8">
        <h2 className="text-title-3 text-heading">{t("new.primaryMember")}</h2>
        <p className="text-fg-muted text-callout mt-1">{t("new.primaryGateBody")}</p>

        <div className="mt-5 grid gap-5">
          <Field label={req(t("new.primaryAccountNumber"))} htmlFor="parent_account_number">
            <Input
              id="parent_account_number"
              autoFocus
              inputMode="numeric"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={t("new.primaryPlaceholder")}
              aria-invalid={found.isError || undefined}
              aria-describedby="parent_status"
            />
          </Field>

          {/* One live region for all three answers, so a screen reader hears
              the verdict change rather than only sighted users seeing it. */}
          <p id="parent_status" role="status" aria-live="polite" className="text-callout -mt-2">
            {looking ? (
              <span className="text-fg-muted flex items-center gap-2">
                <Spinner /> {t("new.lookingUp")}
              </span>
            ) : found.isError ? (
              <span className="text-danger flex items-start gap-2">
                <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
                {errorMessage(found.error, t("new.primaryNotFound"))}
              </span>
            ) : parent ? (
              <span className="text-success flex items-center gap-2">
                <CheckCircle2 className="size-4 shrink-0" aria-hidden />
                {t("new.primaryFound")}
              </span>
            ) : null}
          </p>

          <Field label={t("mem.name")} htmlFor="parent_name">
            <Input id="parent_name" readOnly disabled value={parent ? fullName(parent) : ""} />
          </Field>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label={t("mem.dateOfBirth")} htmlFor="parent_dob">
              <Input id="parent_dob" readOnly disabled value={parent?.date_of_birth ?? ""} />
            </Field>
            <Field label={t("mem.validUntil")} htmlFor="parent_valid">
              <Input
                id="parent_valid"
                readOnly
                disabled
                value={
                  parent
                    ? parent.no_expiry
                      ? t("mem.noExpiry")
                      : (parent.valid_until ?? "")
                    : ""
                }
              />
            </Field>
          </div>
          {parent ? (
            <p className="text-fg-muted text-callout">
              {t("new.inheritsFromPrimary")
                .replace("{tier}", t(TIER_LABELS[parent.membership_tier]))
                .replace("{used}", String(parent.active_supplementaries))}
            </p>
          ) : null}
        </div>
      </Card>

      <div className="mt-8 flex justify-center">
        <Button
          size="lg"
          className="min-w-40"
          disabled={!parent}
          onClick={() => setConfirming(true)}
        >
          {t("login.continue")}
        </Button>
      </div>

      {/* A card is about to be attached to somebody else's account, off a
          number that was typed. That is worth one look before the form. */}
      <Modal
        open={confirming && !!parent}
        onClose={() => setConfirming(false)}
        title={t("new.verifyDetails")}
        description={t("new.verifyBody")}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirming(false)}>
              {t("common.back")}
            </Button>
            <Button onClick={() => parent && onConfirmed(parent)} autoFocus>
              {t("common.confirm")}
            </Button>
          </>
        }
      >
        <dl className="grid gap-3">
          {parent
            ? (
                [
                  [t("new.primaryAccountNumber"), parent.account_number],
                  [t("mem.name"), fullName(parent)],
                  [t("mem.dateOfBirth"), parent.date_of_birth],
                  [
                    t("mem.validUntil"),
                    parent.no_expiry ? t("mem.noExpiry") : (parent.valid_until ?? "—"),
                  ],
                ] as const
              ).map(([label, value]) => (
                <div key={label} className="flex items-baseline justify-between gap-4">
                  <dt className="text-fg-muted text-callout">{label}</dt>
                  <dd className="text-fg text-body text-right font-medium">{value}</dd>
                </div>
              ))
            : null}
        </dl>
      </Modal>
    </div>
  );
}

/**
 * The employee gate: one HR number, checked to be nobody else's before the
 * form opens.
 *
 * Checked here as well as in the service because the number is the first thing
 * asked and the last thing anyone wants to be told about after filling in
 * twenty fields. The list's own `employee_number` filter answers it, so this
 * needs no endpoint of its own.
 */
/** What HR knows about the employee, when the lookup answers; seeds the form. */
export interface HrEmployee {
  first_name: string | null;
  middle_name: string | null;
  last_name: string | null;
  email_address: string | null;
}

function EmployeeGate({
  onConfirmed,
}: {
  onConfirmed: (employeeNumber: string, hr: HrEmployee | null) => void;
}) {
  const t = useTranslation();
  const [number, setNumber] = useState("");
  const [error, setError] = useState<string | null>(null);

  const check = useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ items: Member[] }>("/members/query", {
        employee_number: number.trim(),
        fields: ["id"],
        size: 1,
      });
      if (data.items.length > 0) return { free: false, hr: null };
      // HR's record fills the name and email in; a number HR does not know
      // still enrols, typed by hand, as the legacy form allowed.
      const hr = await api
        .get<HrEmployee>(`/snr/${encodeURIComponent(number.trim())}`)
        .then((r) => r.data)
        .catch(() => null);
      return { free: true, hr };
    },
    onSuccess: ({ free, hr }) =>
      free ? onConfirmed(number.trim(), hr) : setError(t("new.employeeTaken")),
    onError: (err) => setError(errorMessage(err, t("new.employeeCheckFailed"))),
  });

  return (
    <div className="mx-auto mt-10 max-w-xl sm:mt-16">
      <h1 className="text-title-1 text-heading text-center">{t("new.employeeMember")}</h1>
      <Card className="mt-8">
        <h2 className="text-title-3 text-heading">{t("mem.employeeNumber")}</h2>
        <p className="text-fg-muted text-callout mt-1">{t("new.employeeGateBody")}</p>
        <form
          className="mt-5"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            check.mutate();
          }}
        >
          <Field label={req(t("mem.employeeNumber"))} htmlFor="employee_number">
            <Input
              id="employee_number"
              autoFocus
              required
              value={number}
              onChange={(e) => {
                setNumber(e.target.value);
                setError(null);
              }}
              placeholder={t("new.enterEmployeeNumber")}
              aria-invalid={!!error || undefined}
              aria-describedby="employee_status"
            />
          </Field>
          <p
            id="employee_status"
            role="status"
            aria-live="polite"
            className="text-callout mt-2"
          >
            {error ? (
              <span className="text-danger flex items-start gap-2">
                <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
                {error}
              </span>
            ) : null}
          </p>
          <div className="mt-6 flex justify-center">
            <Button
              type="submit"
              size="lg"
              className="min-w-40"
              disabled={!number.trim()}
              loading={check.isPending}
              loadingLabel={t("new.checking")}
            >
              {t("login.continue")}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

function WhoToAdd({
  value,
  onChange,
  onContinue,
}: {
  value: AccountType | null;
  onChange: (value: AccountType) => void;
  onContinue: () => void;
}) {
  const t = useTranslation();
  const choices: { key: AccountType; label: string; icon: ReactNode }[] = [
    {
      key: "primary",
      label: t("mem.primary"),
      icon: <User className="size-24" strokeWidth={1.2} />,
    },
    {
      key: "supplementary",
      label: t("mem.supplementary"),
      icon: <Users className="size-24" strokeWidth={1.2} />,
    },
  ];
  return (
    <div className="mx-auto mt-10 max-w-2xl text-center sm:mt-20">
      <h1 className="text-title-1 text-heading">{t("new.whoToAdd")}</h1>
      <div className="mt-10 flex flex-wrap justify-center gap-6">
        {choices.map((choice) => (
          <button
            key={choice.key}
            type="button"
            onClick={() => onChange(choice.key)}
            aria-pressed={value === choice.key}
            className={cn(
              "bg-surface-raised rounded-card w-full overflow-hidden text-left sm:w-64",
              "transition-[box-shadow,transform] duration-[var(--duration-base)] ease-[var(--ease-out-soft)]",
              value === choice.key
                ? "ring-accent -translate-y-0.5 shadow-[var(--shadow-raised)] ring-2"
                : "shadow-[var(--shadow-lift)] hover:-translate-y-0.5 hover:shadow-[var(--shadow-raised)]",
            )}
          >
            {/* ponytail: an icon on the tinted panel; the illustrations when they are exported. */}
            <div className="bg-accent-soft text-accent grid h-48 place-items-center">
              {choice.icon}
            </div>
            <p className="text-title-3 text-heading py-4 text-center">{choice.label}</p>
          </button>
        ))}
      </div>
      <Button size="lg" className="mt-10 min-w-40" disabled={!value} onClick={onContinue}>
        {t("login.continue")}
      </Button>
    </div>
  );
}

/** The tier's own name, for a sentence about what a supplementary inherits. */
const TIER_LABELS: Record<Tier, TranslationKey> = {
  member: "mem.member",
  affiliates: "mem.affiliates",
  employee: "mem.employee",
  complimentary: "mem.complimentary",
};

const TITLES: Record<Tier, TranslationKey> = {
  member: "new.member",
  affiliates: "new.affiliatesMember",
  employee: "new.employeeMember",
  complimentary: "new.complimentaryMember",
};

function BasicForm({
  tier,
  accountType,
  parent,
  employeeNumber,
  hr = null,
  onCreated,
}: {
  tier: Tier;
  accountType: AccountType;
  /** What the parent gate found, when there was one. */
  parent: PrimaryAccount | null;
  /** What the employee gate cleared, when there was one. */
  employeeNumber: string;
  /** What HR knows about that employee, when the lookup answered. */
  hr?: HrEmployee | null;
  onCreated: (member: Member) => void;
}) {
  const t = useTranslation();
  const byBenefit = BY_BENEFIT.includes(tier);
  const needsAddress = NEEDS_ADDRESS.includes(tier);
  const [v, setV] = useState<Record<string, string>>({
    has_last_name: "true",
    // Only a member-tier account has a membership that means anything; the
    // other three are asked nothing and recorded as gold, like the upload.
    membership_type: "gold",
    ...(byBenefit ? { acquisition_type: "benefits" } : {}),
    // Whatever a gate settled is given, not asked again.
    ...(parent ? { parent_account_number: parent.account_number } : {}),
    ...(employeeNumber ? { employee_number: employeeNumber } : {}),
    ...(hr?.first_name ? { first_name: hr.first_name } : {}),
    ...(hr?.middle_name ? { middle_name: hr.middle_name } : {}),
    ...(hr?.last_name ? { last_name: hr.last_name } : {}),
    ...(hr?.email_address ? { email: hr.email_address } : {}),
  });
  const set = (name: string, value: string) => setV((d) => ({ ...d, [name]: value }));
  const [error, setError] = useState<string | null>(null);

  // The duplicate rule the create is refused by, asked as the name and
  // birthday are typed rather than after twenty fields — the server's own
  // answer, half a second after the last keystroke.
  const identity = {
    first_name: (v.first_name ?? "").trim(),
    last_name: v.has_last_name === "false" ? null : (v.last_name ?? "").trim() || null,
    date_of_birth: v.date_of_birth ?? "",
  };
  const identityKey = JSON.stringify(identity);
  const [settled, setSettled] = useState(identityKey);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(identityKey), 500);
    return () => clearTimeout(timer);
  }, [identityKey]);
  const duplicate = useQuery({
    queryKey: ["/members/exists", settled],
    queryFn: async ({ signal }) =>
      (
        await api.post<{ results: { exists: boolean; account_number: string | null }[] }>(
          "/members/exists",
          { members: [JSON.parse(settled)] },
          { signal },
        )
      ).data.results[0] ?? { exists: false, account_number: null },
    enabled: identity.first_name.length > 0 && identity.date_of_birth.length > 0,
    staleTime: 30_000,
  });
  const taken = duplicate.data?.exists === true;
  // The rules that read more than one field, judged as they are typed and said
  // under the field that fails them — the submit re-checks every one.
  const tooYoung = !!v.date_of_birth && years(v.date_of_birth) < MINIMUM_AGE;
  const needsMobile =
    !v.mobile && (v.phone_consent === "true" || v.only_important_updates === "true");
  const needsEmail = !v.email && v.email_consent === "true";
  const [consent, setConsent] = useState<ConsentText | null>(null);

  const create = useMutation({
    mutationFn: async () => {
      const hasLast = v.has_last_name !== "false";
      const body = {
        membership_tier: tier,
        membership_type: v.membership_type || "gold",
        account_type: accountType,
        employee_number: v.employee_number || null,
        company_name: v.company_name || null,
        // Not a column: it is who the complimentary note the create files says
        // signed the membership off. See `docs/member-create-flow.md` §3.
        approved_by_id: v.approved_by_id ? Number(v.approved_by_id) : null,
        prefix: v.prefix || null,
        suffix: v.suffix || null,
        first_name: v.first_name,
        middle_name: v.middle_name || null,
        last_name: hasLast ? v.last_name || null : null,
        date_of_birth: v.date_of_birth || null,
        gender: v.gender || null,
        acquisition_type: v.acquisition_type || null,
        mobile_number: v.mobile ? `+63${v.mobile}` : null,
        email: v.email || null,
        unit_number: v.unit_number || null,
        building_name: v.building_name || null,
        address_street: v.address_street || null,
        city_id: v.city_id ? Number(v.city_id) : null,
        barangay_id: v.barangay_id ? Number(v.barangay_id) : null,
        parent_account_number: v.parent_account_number || null,
        temporary_card_number: v.temporary_card_number || null,
        email_consent: v.email_consent === "true",
        phone_consent: v.phone_consent === "true",
        only_important_updates: v.only_important_updates === "true",
        // The two agreements are still required to proceed, below; nothing
        // stores the answer, so nothing is sent.
        id_card_image: v.id_card_image || null,
        profile_image: v.profile_image || null,
      };
      return (await api.post<Member>("/members", body)).data;
    },
    onSuccess: (member) => {
      toast.success(
        t("new.enrolled")
          .replace("{name}", fullName(member))
          .replace("{number}", member.account_number),
      );
      onCreated(member);
    },
    onError: (err) => setError(errorMessage(err, t("new.enrolFailed"))),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (taken) {
      setError(t("new.duplicateMember"));
      return;
    }
    // The browser has already checked every `required` field; what is left is
    // the rules that read more than one of them.
    if (years(v.date_of_birth) < MINIMUM_AGE) {
      setError(t("new.tooYoung"));
      return;
    }
    // Consent to be written to makes that way of writing required; with no
    // consent at all, one of the two is still needed to reach the member.
    if (!v.mobile && (v.phone_consent === "true" || v.only_important_updates === "true")) {
      setError(t("new.mobileRequired"));
      return;
    }
    if (!v.email && v.email_consent === "true") {
      setError(t("new.emailRequired"));
      return;
    }
    if (!v.mobile && !v.email) {
      setError(t("new.contactRequired"));
      return;
    }
    if (v.agree_term !== "true" || v.agree_personal_info !== "true") {
      setError(t("new.consentsRequired"));
      return;
    }
    // Somebody at the counter was seen and photographed; a canvasser hands
    // over a stand-in card there and then. The service checks both again.
    if (v.acquisition_type === "walk_in" && (!v.id_card_image || !v.profile_image)) {
      setError(t("new.walkInNeedsPictures"));
      return;
    }
    if (v.acquisition_type === "canvassing" && !v.temporary_card_number) {
      setError(t("new.canvassingNeedsCard"));
      return;
    }
    create.mutate();
  };

  return (
    <form onSubmit={submit} className="mx-auto mt-12 max-w-4xl">
      <h1 className="text-title-1 text-heading text-center">
        {accountType === "primary" ? t("mem.primary") : t("mem.supplementary")}{" "}
        {t(TITLES[tier])}
      </h1>

      <div className="mt-10 grid gap-5 md:grid-cols-2">
        {/* A benefit enrolment is vouched for by the club, not by an ID. */}
        {byBenefit ? null : (
          <Upload
            title={t("new.idCard")}
            action={t("new.scanId")}
            value={v.id_card_image}
            onChange={(data) => set("id_card_image", data)}
          />
        )}
        <Upload
          title={t("new.photo")}
          action={t("new.capturePhoto")}
          capture
          value={v.profile_image}
          onChange={(data) => set("profile_image", data)}
        />
      </div>

      <Card className="mt-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-title-3 text-heading">{t("md.basicInformation")}</h2>
            <p className="text-fg-muted text-subhead mt-1">{t("new.autoFill")}</p>
          </div>
          <div className="flex w-full flex-wrap gap-4 sm:w-auto">
            {/* Only the member tier is sold; the other three are the tier itself. */}
            {tier === "member" ? (
              <div className="w-full sm:w-52">
                <Choice
                  name="membership_type"
                  label={req(t("mem.membershipType"))}
                  placeholder={t("new.selectMembership")}
                  options={MEMBERSHIP_TYPE(t)}
                  required
                  v={v}
                  set={set}
                />
              </div>
            ) : null}
            <div className="w-full sm:w-64">
              {byBenefit ? (
                // Fixed, not chosen: these three tiers are enrolled as a
                // benefit and nothing else, so a select would be one option.
                <Field label={t("mem.acquisitionType")} htmlFor="acquisition_type">
                  <Input id="acquisition_type" value={t("mem.benefits")} disabled readOnly />
                </Field>
              ) : (
                <Choice
                  name="acquisition_type"
                  label={req(t("mem.acquisitionType"))}
                  placeholder={t("new.selectAcquisition")}
                  options={ACQUISITION(t)}
                  required
                  v={v}
                  set={set}
                />
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 grid gap-x-8 gap-y-5 md:grid-cols-2">
          {accountType === "supplementary" ? (
            <div className="md:col-span-2">
              <Field label={t("new.primaryAccountNumber")} htmlFor="parent_account_number">
                <Input
                  id="parent_account_number"
                  readOnly
                  disabled
                  value={v.parent_account_number ?? ""}
                />
              </Field>
              <p className="text-fg-muted text-callout mt-2">{t("new.inheritedFromPrimary")}</p>
            </div>
          ) : null}
          {/* The one field the tier adds. Every tier has at most one, so this
              is a switch rather than a section per tier. */}
          {tier === "employee" ? (
            <Field label={t("mem.employeeNumber")} htmlFor="employee_number">
              {/* Settled by the gate, and checked to be nobody else's there. */}
              <Input id="employee_number" readOnly disabled value={v.employee_number ?? ""} />
            </Field>
          ) : null}
          {tier === "affiliates" ? (
            <Field label={t("mem.companyName")} htmlFor="company_name">
              <Input
                id="company_name"
                value={v.company_name ?? ""}
                onChange={(e) => set("company_name", e.target.value)}
                placeholder={t("new.enterCompanyName")}
              />
            </Field>
          ) : null}
          {tier === "complimentary" ? (
            <Field label={req(t("new.approvedBy"))} htmlFor="approved_by_id">
              {/* A name from a configured list, not free text: the answer is
                  filed as the member's complimentary note, which holds it by
                  id. Legacy called the same list "S&R family owners". */}
              <FkSelect
                id="approved_by_id"
                required
                endpoint="/reasons/compact"
                params={{ kind: "complimentary_approved_by" }}
                value={v.approved_by_id ?? ""}
                onChange={(next) => set("approved_by_id", next)}
                placeholder={t("new.selectApprovedBy")}
              />
            </Field>
          ) : null}
          <Choice
            name="prefix"
            label={t("mem.prefix")}
            placeholder={t("new.selectPrefix")}
            options={PREFIX(t)}
            v={v}
            set={set}
          />
          <Choice
            name="suffix"
            label={t("mem.suffix")}
            placeholder={t("new.selectSuffix")}
            options={SUFFIX()}
            v={v}
            set={set}
          />
          <Field label={req(t("mem.firstName"))} htmlFor="first_name">
            <Input
              id="first_name"
              required
              value={v.first_name ?? ""}
              onChange={(e) => set("first_name", e.target.value)}
              placeholder={t("new.enterFirstName")}
            />
          </Field>
          <Field label={t("mem.middleName")} htmlFor="middle_name">
            <Input
              id="middle_name"
              value={v.middle_name ?? ""}
              onChange={(e) => set("middle_name", e.target.value)}
              placeholder={t("new.enterMiddleName")}
            />
          </Field>
          <Field
            label={
              <span className="flex items-center justify-between gap-3">
                {req(t("mem.lastName"))}
                <label className="text-fg-muted text-footnote flex items-center gap-2 font-normal">
                  {t("new.toggleNoLastName")}
                  <Toggle
                    checked={v.has_last_name !== "false"}
                    onChange={(on) => set("has_last_name", on ? "true" : "false")}
                  />
                </label>
              </span>
            }
            htmlFor="last_name"
          >
            <Input
              id="last_name"
              required={v.has_last_name !== "false"}
              disabled={v.has_last_name === "false"}
              value={v.last_name ?? ""}
              onChange={(e) => set("last_name", e.target.value)}
              placeholder={t("new.enterLastName")}
            />
          </Field>
          <Field label={req(t("mem.dateOfBirth"))} htmlFor="date_of_birth">
            <Input
              id="date_of_birth"
              type="date"
              required
              max={new Date().toISOString().slice(0, 10)}
              value={v.date_of_birth ?? ""}
              onChange={(e) => set("date_of_birth", e.target.value)}
              aria-describedby="identity_status"
            />
            <p
              id="identity_status"
              role="status"
              aria-live="polite"
              className="text-callout mt-2"
            >
              {taken ? (
                <span className="text-danger flex items-start gap-2">
                  <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
                  {t("new.duplicateMember")}
                  {duplicate.data?.account_number ? ` (${duplicate.data.account_number})` : ""}
                </span>
              ) : tooYoung ? (
                <span className="text-danger flex items-start gap-2">
                  <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
                  {t("new.tooYoung")}
                </span>
              ) : null}
            </p>
          </Field>
          <Choice
            name="gender"
            label={req(t("mem.gender"))}
            placeholder={t("new.selectGender")}
            options={GENDER(t)}
            required
            v={v}
            set={set}
          />
          <Field label={t("mem.mobileNumber")} htmlFor="mobile">
            <div className="flex">
              <span className="rounded-field border-line bg-surface-sunken text-fg text-body inline-flex items-center rounded-r-none border border-r-0 px-3.5">
                +63
              </span>
              <Input
                id="mobile"
                inputMode="numeric"
                pattern="9[0-9]{9}"
                maxLength={10}
                className="rounded-l-none"
                value={v.mobile ?? ""}
                onChange={(e) => set("mobile", e.target.value.replace(/\D/g, ""))}
                placeholder="9XXXXXXXXX"
                aria-invalid={needsMobile || undefined}
              />
            </div>
            {needsMobile ? <FieldError text={t("new.mobileRequired")} /> : null}
          </Field>
          <div className="md:col-span-2">
            <Field label={t("mem.email")} htmlFor="email">
              <Input
                id="email"
                type="email"
                maxLength={254}
                value={v.email ?? ""}
                onChange={(e) => set("email", e.target.value)}
                placeholder={t("new.enterEmail")}
                aria-invalid={needsEmail || undefined}
              />
              {needsEmail ? <FieldError text={t("new.emailRequired")} /> : null}
            </Field>
            <p className="text-fg-muted text-callout mt-2">
              <span className="text-danger">*</span> {t("new.contactNote")}
            </p>
          </div>
          {/* A canvasser hands over a stand-in card at the door; the real one
              is printed later. The field appears only when it is owed. */}
          {v.acquisition_type === "canvassing" ? (
            <Field label={req(t("mem.temporaryCardNumber"))} htmlFor="temporary_card_number">
              <Input
                id="temporary_card_number"
                required
                inputMode="numeric"
                pattern="[0-9]{11}"
                maxLength={11}
                title={t("new.temporaryCardDigits")}
                value={v.temporary_card_number ?? ""}
                onChange={(e) =>
                  set("temporary_card_number", e.target.value.replace(/\D/g, "").slice(0, 11))
                }
                placeholder={t("new.enterTemporaryCard")}
              />
            </Field>
          ) : null}
          {/* An affiliate and a complimentary member are enrolled on the
              strength of who vouched for them, so no address is taken. */}
          {needsAddress ? (
            <>
              <Field label={t("mem.unitNumber")} htmlFor="unit_number">
                <Input
                  id="unit_number"
                  value={v.unit_number ?? ""}
                  onChange={(e) => set("unit_number", e.target.value)}
                  placeholder={t("new.enterUnitNumber")}
                />
              </Field>
              <Field label={t("mem.buildingName")} htmlFor="building_name">
                <Input
                  id="building_name"
                  value={v.building_name ?? ""}
                  onChange={(e) => set("building_name", e.target.value)}
                  placeholder={t("new.enterBuildingName")}
                />
              </Field>
              <Field label={req(t("mem.addressStreet"))} htmlFor="address_street">
                <Input
                  id="address_street"
                  required
                  value={v.address_street ?? ""}
                  onChange={(e) => set("address_street", e.target.value)}
                  placeholder={t("new.enterAddress")}
                />
              </Field>
              <Field label={req(t("mem.city"))} htmlFor="city_id">
                <FkSelect
                  id="city_id"
                  required
                  endpoint="/cities/compact"
                  value={v.city_id ?? ""}
                  onChange={(next) => {
                    set("city_id", next);
                    set("barangay_id", "");
                  }}
                  placeholder={t("new.selectCity")}
                />
              </Field>
              <Field label={req(t("mem.barangay"))} htmlFor="barangay_id">
                <FkSelect
                  id="barangay_id"
                  required
                  endpoint="/barangays/compact"
                  params={{ city_id: v.city_id }}
                  disabled={!v.city_id}
                  value={v.barangay_id ?? ""}
                  onChange={(next) => set("barangay_id", next)}
                  placeholder={v.city_id ? t("new.selectBarangay") : t("new.selectCityFirst")}
                />
              </Field>
            </>
          ) : null}
        </div>
      </Card>

      <Card className="mt-8">
        <h2 className="text-title-3 text-heading">{t("new.receiveUpdates")}</h2>
        <p className="text-fg-muted text-callout mt-2 max-w-3xl">{t("new.receiveBody")}</p>
        <div className="mt-4 flex flex-wrap gap-6">
          <Check name="email_consent" label={t("mem.email")} v={v} set={set} />
          <Check name="phone_consent" label={t("mem.mobileNumber")} v={v} set={set} />
        </div>
        <div className="mt-4">
          <Check
            name="only_important_updates"
            label={t("mem.onlyImportantUpdates")}
            v={v}
            set={set}
          />
        </div>
      </Card>

      <Card className="mt-8">
        <h2 className="text-title-3 text-heading">{t("new.privacyConsent")}</h2>
        <div className="mt-4 grid gap-4">
          <Check
            name="agree_term"
            v={v}
            set={set}
            label={
              <>
                {t("new.agreeTerms")}{" "}
                <TextLink onClick={() => setConsent("terms")}>
                  {t("new.termsAndConditions")}
                </TextLink>{" "}
                {t("new.and")}{" "}
                <TextLink onClick={() => setConsent("privacy")}>
                  {t("new.privacyPolicy")}
                </TextLink>
                .
              </>
            }
          />
          <Check
            name="agree_personal_info"
            v={v}
            set={set}
            label={
              <>
                {t("new.agreePersonal")}{" "}
                <TextLink onClick={() => setConsent("reasons")}>
                  {t("new.statedReasons")}
                </TextLink>
                .
              </>
            }
          />
        </div>
      </Card>

      <ConsentTextModal text={consent} onClose={() => setConsent(null)} />

      {error ? (
        <p
          role="alert"
          className="bg-danger-soft text-danger text-callout mx-auto mt-6 flex max-w-md items-start gap-2 rounded-xl px-3.5 py-3"
        >
          <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>{error}</span>
        </p>
      ) : null}
      <div className="mt-8 flex justify-center pb-10">
        <Button
          type="submit"
          size="lg"
          className="min-w-40"
          loading={create.isPending}
          loadingLabel={t("new.saving")}
        >
          {t("login.continue")}
        </Button>
      </div>
    </form>
  );
}

function Card({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <section
      className={cn(
        "bg-surface-raised rounded-card p-7 shadow-[var(--shadow-lift)]",
        className,
      )}
    >
      {children}
    </section>
  );
}

function Upload({
  title,
  action,
  capture,
  value,
  onChange,
}: {
  title: string;
  action: string;
  capture?: boolean;
  value: string | undefined;
  onChange: (data: string) => void;
}) {
  const t = useTranslation();
  const [over, setOver] = useState(false);
  const take = (file: File | undefined) => {
    if (file && file.type.startsWith("image/")) void readImage(file, 1200).then(onChange);
  };
  const pick = (input: HTMLInputElement) => {
    take(input.files?.[0]);
    input.value = "";
  };
  return (
    <Card className={cn(over && "ring-accent ring-2")}>
      <div className="flex items-start justify-between gap-3">
        <h2 className="text-fg text-body font-semibold">{title}</h2>
        {capture ? (
          // What a usable photograph needs, as legacy listed it beside the camera.
          <details className="text-callout relative">
            <summary className="text-fg-muted hover:text-fg flex cursor-pointer list-none items-center gap-1">
              <Info className="size-4" aria-hidden /> {t("new.captureGuide")}
            </summary>
            <ul className="bg-surface-raised rounded-card text-fg-muted absolute right-0 z-10 mt-2 w-64 list-disc p-4 pl-8 shadow-[var(--shadow-raised)]">
              <li>{t("new.guide1")}</li>
              <li>{t("new.guide2")}</li>
              <li>{t("new.guide3")}</li>
              <li>{t("new.guide4")}</li>
            </ul>
          </details>
        ) : null}
      </div>
      {value ? (
        <div className="relative mt-4 flex justify-center">
          <img src={value} alt="" className="h-40 max-w-full rounded-xl object-contain" />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute top-2 right-2 grid size-9 place-items-center rounded-full bg-black/55 text-white transition-colors hover:bg-black/70"
          >
            <X className="size-4" aria-hidden />
            <span className="sr-only">{t("new.removePicture")}</span>
          </button>
        </div>
      ) : null}
      <label
        className="mt-4 flex cursor-pointer flex-col items-center gap-3 py-2 text-center"
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          take(e.dataTransfer.files[0]);
        }}
      >
        <span className="text-accent text-body font-semibold">{action}</span>
        {value ? null : <CloudUpload className="text-fg-subtle size-7" aria-hidden />}
        <span className="text-fg-muted text-callout">
          <span className="text-accent font-semibold underline">{t("new.clickToUpload")}</span>{" "}
          {t("new.orDrag")}
        </span>
        <span className="text-fg-subtle text-footnote">{t("new.pngOrJpg")}</span>
        <input
          type="file"
          accept="image/*"
          capture={capture ? "user" : undefined}
          className="sr-only"
          onChange={(e) => pick(e.target)}
        />
      </label>
    </Card>
  );
}

/** One sentence under a field, in the danger colour, for a rule the field breaks. */
function FieldError({ text }: { text: string }) {
  return (
    <p role="status" className="text-danger text-callout mt-2 flex items-start gap-2">
      <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
      {text}
    </p>
  );
}

/** A link inside a sentence that opens something rather than going somewhere. */
function TextLink({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} className="text-accent underline">
      {children}
    </button>
  );
}

function Choice({
  name,
  label,
  placeholder,
  options,
  required,
  v,
  set,
}: {
  name: string;
  label: ReactNode;
  placeholder: string;
  options: { value: string; label: string }[];
  required?: boolean;
  v: Record<string, string>;
  set: (name: string, value: string) => void;
}) {
  return (
    <Field label={label} htmlFor={name}>
      <Select
        id={name}
        required={required}
        value={v[name] ?? ""}
        onChange={(e) => set(name, e.target.value)}
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </Select>
    </Field>
  );
}

function Check({
  name,
  label,
  v,
  set,
}: {
  name: string;
  label: ReactNode;
  v: Record<string, string>;
  set: (name: string, value: string) => void;
}) {
  return (
    <label className="text-fg text-body flex items-start gap-3">
      <input
        type="checkbox"
        className="accent-accent mt-1 size-5 shrink-0"
        checked={v[name] === "true"}
        onChange={(e) => set(name, e.target.checked ? "true" : "false")}
      />
      <span>{label}</span>
    </label>
  );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (on: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={cn(
        "relative h-5 w-9 rounded-full transition-colors",
        checked ? "bg-accent" : "bg-ink-300",
      )}
    >
      <span
        className={cn(
          "absolute top-0.5 size-4 rounded-full bg-white transition-transform",
          checked ? "translate-x-4" : "translate-x-0.5",
        )}
      />
    </button>
  );
}

export default NewMemberPage;
